import {
  ALERT_MARKER_CAP,
  ALERT_MARKER_LABELS,
  ALERT_SEVERITY_COLORS,
  AlertTypeEnums,
} from '../common/constants'

const HIDDEN_RANGE_COLOR = '#FBBf24'
const HIDDEN_RANGE_OPACITY = 0.15

export interface AlertMarker {
  timestamp: Date
  alertType: string
  severity: string
  isRecovery: boolean
  title: string
}

function getAlertMarkerColor(marker: AlertMarker): string {
  if (marker.isRecovery) return ALERT_SEVERITY_COLORS.recovery
  if (marker.alertType === AlertTypeEnums.BATTERY_NO_POWER) return ALERT_SEVERITY_COLORS.critical
  if (marker.alertType === AlertTypeEnums.BATTERY_LOW) return ALERT_SEVERITY_COLORS.warning
  if (marker.alertType === AlertTypeEnums.DEVICE_OFFLINE) return ALERT_SEVERITY_COLORS.offline
  return ALERT_SEVERITY_COLORS.critical
}

/**
 * Converts alert history records into CanvasJS X-axis striplines.
 * Recovery events render as thin dashed green lines; alert firings as solid coloured lines.
 * Caps to ALERT_MARKER_CAP most-recent markers to protect rendering performance.
 */
export function alertMarkersToStripLines(markers: AlertMarker[] | undefined): any[] {
  if (!markers || markers.length === 0) return []

  const sorted = [...markers].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
  if (sorted.length > ALERT_MARKER_CAP) {
    console.warn(`[alertMarkersToStripLines] ${sorted.length} markers exceeded cap; truncating to ${ALERT_MARKER_CAP}`)
  }
  const capped = sorted.slice(0, ALERT_MARKER_CAP)

  return capped.map((marker) => {
    const color = getAlertMarkerColor(marker)
    const label = ALERT_MARKER_LABELS[marker.alertType] || marker.title || marker.alertType
    return {
      value: marker.timestamp.getTime(),
      color,
      lineDashType: marker.isRecovery ? 'dash' : 'solid',
      thickness: marker.isRecovery ? 1 : 2,
      label,
      labelFontColor: color,
      labelFontSize: 10,
      labelAlign: 'near',
      labelWrap: false,
      showOnTop: true,
    }
  })
}

/**
 * Converts a hidden timestamp map into contiguous range stripLines for CanvasJS.
 * Groups consecutive hidden timestamps (within 10-minute gaps) into ranges.
 */
export function getHiddenRangeStripLines(hiddenMap: Record<string, boolean>): any[] {
  const hiddenTimestamps = Object.keys(hiddenMap)
    .filter((key) => hiddenMap[key])
    .map(Number)
    .sort((a, b) => a - b)

  if (hiddenTimestamps.length === 0) return []

  // Detect typical interval between hidden timestamps for gap tolerance
  const intervals: number[] = []
  for (let i = 1; i < hiddenTimestamps.length && intervals.length < 10; i++) {
    intervals.push(hiddenTimestamps[i] - hiddenTimestamps[i - 1])
  }
  const typicalInterval = intervals.length > 0 ? Math.min(...intervals) : 900
  // Use 1.5x the typical interval as gap tolerance for grouping
  const gapTolerance = typicalInterval * 1.5

  const ranges: { start: number; end: number }[] = []
  let rangeStart = hiddenTimestamps[0]
  let rangeEnd = hiddenTimestamps[0]

  for (let i = 1; i < hiddenTimestamps.length; i++) {
    const gap = hiddenTimestamps[i] - hiddenTimestamps[i - 1]
    if (gap > gapTolerance) {
      ranges.push({ start: rangeStart, end: rangeEnd })
      rangeStart = hiddenTimestamps[i]
    }
    rangeEnd = hiddenTimestamps[i]
  }
  ranges.push({ start: rangeStart, end: rangeEnd })

  // Add half-interval padding so even single-point ranges have visible width
  const padding = typicalInterval / 2

  return ranges.map((range) => ({
    startValue: new Date((range.start - padding) * 1000),
    endValue: new Date((range.end + padding) * 1000),
    color: HIDDEN_RANGE_COLOR,
    opacity: HIDDEN_RANGE_OPACITY,
  }))
}

/**
 * Checks if a hidden map has any hidden entries.
 */
export function hasHiddenEntries(hiddenMap: Record<string, boolean> | undefined): boolean {
  if (!hiddenMap) return false
  return Object.values(hiddenMap).some((v) => v)
}

const MS_PER_DAY = 24 * 60 * 60 * 1000
const AXIS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function pad2(value: number): string {
  return String(value).padStart(2, '0')
}

/**
 * Render one X-axis tick, choosing the granularity from how much time the axis
 * spans rather than from the previous tick.
 *
 * The previous implementation compared each tick's weekday against the last one
 * and printed a date only when it differed. On a wide range CanvasJS spaces the
 * ticks a whole number of weeks apart, so every tick landed on the same weekday,
 * the comparison never fired, and the axis read "00:00" all the way across.
 */
export function formatAxisTimeLabel(date: Date, spanMs: number): string {
  const time = `${pad2(date.getHours())}:${pad2(date.getMinutes())}`
  const day = `${AXIS_MONTHS[date.getMonth()]} ${date.getDate()}`
  const isMidnight = date.getHours() === 0 && date.getMinutes() === 0

  // Inside two days the date is redundant — every tick belongs to today or yesterday.
  if (spanMs <= 2 * MS_PER_DAY) return time
  // Past a year the month/day alone is ambiguous.
  if (spanMs > 400 * MS_PER_DAY) return `${day}, ${date.getFullYear()}`
  // Otherwise date the midnight ticks and clock the ones in between.
  if (isMidnight) return day
  return spanMs <= 14 * MS_PER_DAY ? time : `${day} ${time}`
}

/**
 * Best-effort axis span (ms) from what CanvasJS hands the label formatter: the
 * axis options we configured first, then the chart's own computed bounds.
 */
function resolveAxisSpanMs(e: any): number {
  // The live axis first, so labels re-granularise when the user zooms in; the
  // options we configured are the fallback for the first paint.
  const candidates = [
    [e?.chart?.axisX?.[0]?.viewportMinimum, e?.chart?.axisX?.[0]?.viewportMaximum],
    [e?.chart?.axisX?.[0]?.minimum, e?.chart?.axisX?.[0]?.maximum],
    [e?.axis?.viewportMinimum, e?.axis?.viewportMaximum],
    [e?.axis?.minimum, e?.axis?.maximum],
  ]

  for (const [min, max] of candidates) {
    const from = Number(min)
    const to = Number(max)
    if (Number.isFinite(from) && Number.isFinite(to) && to > from) return to - from
  }

  return MS_PER_DAY
}

/**
 * CanvasJS `axisX.labelFormatter` that adapts to the visible range. Pass the
 * span when it is known up front (the chart's viewport); otherwise it is read
 * off the axis at paint time, which also keeps zoomed-in labels correct.
 */
export function createAxisLabelFormatter(spanMs?: number) {
  return function (e: any): string {
    const span = Number.isFinite(spanMs as number) && (spanMs as number) > 0 ? (spanMs as number) : resolveAxisSpanMs(e)
    return formatAxisTimeLabel(new Date(e.value), span)
  }
}
